import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, AlertTriangle, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';

// A pinnable dashboard tile summarising this week's Forecast Sentinel projected
// issues. Fetches live so a pinned dashboard always shows the current week.
export function SentinelSummaryTile() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await api.getForecastSentinelsSummary()); }
    catch { setData({ count: 0, issues: [] }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const count = data?.count || 0;
  const issues = data?.issues || [];

  return (
    <div className="h-full w-full flex flex-col" data-testid="sentinel-summary-tile">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {count > 0 ? <AlertTriangle className="w-4 h-4 text-fuchsia-400 shrink-0" /> : <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />}
          <span className="text-sm font-heading font-semibold text-foreground truncate">Projected issues this week</span>
        </div>
        <button onClick={load} disabled={loading} className="text-[10px] text-indigo-400 hover:text-indigo-300 rounded px-1.5 py-0.5 disabled:opacity-50 print:hidden" title="Refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-4xl font-heading font-bold text-foreground" data-testid="sentinel-summary-count">{count}</span>
        <span className="text-xs text-muted-foreground">forward-looking {count === 1 ? 'issue' : 'issues'}</span>
      </div>
      <div className="flex-1 min-h-0 overflow-auto space-y-1.5">
        {count === 0 ? (
          <p className="text-xs text-muted-foreground">No projected issues this week — every watched metric is on track.</p>
        ) : issues.slice(0, 6).map((it, i) => (
          <button
            key={i}
            onClick={() => navigate('/forecast-sentinels')}
            className="w-full text-left rounded-lg border border-border/30 bg-muted/20 hover:bg-muted/40 px-2.5 py-1.5"
            data-testid={`sentinel-summary-issue-${i}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-foreground truncate">{it.measure_label || it.measure}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded ${it.confidence === 'High' ? 'text-emerald-300 bg-emerald-500/10' : it.confidence === 'Medium' ? 'text-amber-300 bg-amber-500/10' : 'text-muted-foreground bg-muted'}`}>{it.confidence || 'Unknown'}</span>
            </div>
            {it.reason && <p className="text-[10px] text-muted-foreground truncate mt-0.5">{it.reason}{it.period ? ` · by ${it.period}` : ''}</p>}
          </button>
        ))}
      </div>
    </div>
  );
}
